import { useState } from "react";
import { Navigate } from "react-router-dom";
import { BookOpenText, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { MANUAL_SECTIONS, MANUAL_UPDATED } from "../content/manual";

export default function ManualPage() {
  const { hasPerm } = useAuth();
  const [search, setSearch] = useState("");

  if (!hasPerm("manual.view")) return <Navigate to="/" replace />;

  const q = search.toLowerCase();
  const sections = MANUAL_SECTIONS.map((s) => ({
    ...s,
    items: q ? s.items.filter((i) => i.h.toLowerCase().includes(q) || i.p.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)) : s.items,
  })).filter((s) => s.items.length > 0);

  const jump = (id) => document.getElementById(`manual-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Help</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <BookOpenText size={30} weight="duotone" className="text-indigo-600" /> Admin manual
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Everything Team can do and how to run it. Kept up to date as features ship — last updated {MANUAL_UPDATED}.</p>
        </div>
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="manual-search-input" placeholder="Search the manual…" className="h-10 w-64 pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="flex gap-8 items-start">
        <aside className="hidden lg:block w-56 shrink-0 sticky top-8 space-y-1" data-testid="manual-nav">
          {MANUAL_SECTIONS.map((s) => (
            <button key={s.id} data-testid={`manual-nav-${s.id}`} onClick={() => jump(s.id)}
              className="block w-full text-left text-sm px-3 py-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              {s.title}
            </button>
          ))}
        </aside>

        <div className="flex-1 min-w-0 space-y-6" data-testid="manual-content">
          {sections.map((s) => (
            <section key={s.id} id={`manual-${s.id}`} className="bg-card border border-border rounded-md scroll-mt-6">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="font-heading font-semibold tracking-tight">{s.title}</h2>
              </div>
              <div className="divide-y divide-border">
                {s.items.map((i) => (
                  <div key={i.h} className="px-6 py-4">
                    <h3 className="text-sm font-semibold mb-1">{i.h}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{i.p}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {sections.length === 0 && (
            <div className="bg-card border border-border rounded-md px-6 py-10 text-sm text-muted-foreground" data-testid="manual-no-results">
              Nothing in the manual matches "{search}".
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
